/**
 * Hook for Conferência Verificação detail page.
 * Manages conference detail, items, lacres, scanning (two-read), and finalization.
 */
import { useState, useEffect, useCallback, useRef } from 'react';
import {
  getConferenciaVerificacao,
  listarItensConferenciaVerif,
  getLacresDisponiveis,
  selecionarLacres,
  atualizarConferenciaVerificacao,
  registrarLeitura,
  finalizarConferencia,
  toggleConferido,
  atualizarItemConferencia,
  reiniciarConferencia,
  ConferenciaVerificacaoDetail,
  ItemConferenciaVerif,
  LacreDisponivel,
} from '../services/conferenciaService';
import {
  parseGS1, needsSecondRead, isSecondRead, canMerge, mergeGS1Data,
  type GS1Data,
} from '../utils/gs1Parser';
import { initSoundFeedback, playSuccess, playError, playAttention } from '../utils/soundFeedback';
import { toast } from 'react-hot-toast';
import type { LastReadInfo } from '../components/conferencia/LastReadCard';
import type { ScanLogEntry, ScanLogResult } from '../components/conferencia/ScanDebugPanel';

const TWO_READ_TIMEOUT_MS = 25_000;
const MAX_SCAN_LOGS = 20;

let scanLogCounter = 0;

export function useConferenciaDetail(conferenciaId: number | null) {
  const [conferencia, setConferencia] = useState<ConferenciaVerificacaoDetail | null>(null);
  const [itens, setItens] = useState<ItemConferenciaVerif[]>([]);
  const [lacresDisponiveis, setLacresDisponiveis] = useState<LacreDisponivel[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingItens, setLoadingItens] = useState(false);
  const [saving, setSaving] = useState(false);
  const [lastScannedItemId, setLastScannedItemId] = useState<number | null>(null);
  const [soundReady, setSoundReady] = useState(false);

  // Two-read state machine
  const [pendingFirstRead, setPendingFirstRead] = useState<GS1Data | null>(null);
  const pendingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingCountdownRef = useRef<ReturnType<typeof setInterval> | null>(null);

  // Feedback
  const [lastReadInfo, setLastReadInfo] = useState<LastReadInfo | null>(null);
  const [scanLogs, setScanLogs] = useState<ScanLogEntry[]>([]);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [modifiedItems, setModifiedItems] = useState<Map<number, number>>(new Map());
  const [originalItens, setOriginalItens] = useState<ItemConferenciaVerif[]>([]);

  const scanInputRef = useRef<HTMLInputElement>(null);

  // ── Helpers ──

  const addScanLog = useCallback((
    rawBarcode: string, gs1: GS1Data, result: ScanLogResult, message: string
  ) => {
    const entry: ScanLogEntry = {
      id: ++scanLogCounter,
      timestamp: new Date(),
      rawBarcode,
      gtin: gs1.gtin,
      refCode: gs1.refCode,
      lote: gs1.lote,
      validade: gs1.validade
        ? gs1.validade.toLocaleDateString('pt-BR')
        : null,
      result,
      message,
    };
    setScanLogs(prev => [entry, ...prev].slice(0, MAX_SCAN_LOGS));
  }, []);

  const updateLastLog = useCallback((result: ScanLogResult, message: string) => {
    setScanLogs(prev => {
      if (prev.length === 0) return prev;
      const updated = [...prev];
      updated[0] = { ...updated[0], result, message };
      return updated;
    });
  }, []);

  const clearPending = useCallback(() => {
    setPendingFirstRead(null);
    if (pendingTimeoutRef.current) {
      clearTimeout(pendingTimeoutRef.current);
      pendingTimeoutRef.current = null;
    }
    if (pendingCountdownRef.current) {
      clearInterval(pendingCountdownRef.current);
      pendingCountdownRef.current = null;
    }
  }, []);

  const clearScanLogs = useCallback(() => {
    setScanLogs([]);
  }, []);

  // ── Data loading ──

  const loadDetail = useCallback(async () => {
    if (!conferenciaId) return;
    try {
      setLoading(true);
      const [detail, items, lacres] = await Promise.all([
        getConferenciaVerificacao(conferenciaId),
        listarItensConferenciaVerif(conferenciaId),
        getLacresDisponiveis(conferenciaId),
      ]);
      setConferencia(detail);
      setItens(items);
      setLacresDisponiveis(lacres);
    } catch {
      // toasts in service
    } finally {
      setLoading(false);
    }
  }, [conferenciaId]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  // Cleanup pending timeout on unmount
  useEffect(() => {
    return () => {
      clearPending();
    };
  }, [clearPending]);

  const reloadItens = useCallback(async () => {
    if (!conferenciaId) return;
    try {
      setLoadingItens(true);
      const items = await listarItensConferenciaVerif(conferenciaId);
      setItens(items);
    } catch {
      // handled
    } finally {
      setLoadingItens(false);
    }
  }, [conferenciaId]);

  // ── Sound init ──

  const handleInitSound = useCallback(async () => {
    if (soundReady) return;
    await initSoundFeedback();
    setSoundReady(true);
  }, [soundReady]);

  // ── Select lacres ──

  const handleSelectLacres = useCallback(async (lacres: string[]) => {
    if (!conferenciaId) return;
    try {
      setSaving(true);
      const result = await selecionarLacres(conferenciaId, lacres);
      if (result.success) {
        toast.success(result.message);
        await reloadItens();
        const newLacres = await getLacresDisponiveis(conferenciaId);
        setLacresDisponiveis(newLacres);
      }
    } catch {
      // handled
    } finally {
      setSaving(false);
    }
  }, [conferenciaId, reloadItens]);

  // ── Update admin fields ──

  const handleUpdateFields = useCallback(async (data: {
    notas_fiscais?: string;
    data_recebimento_nf?: string;
    observacoes?: string;
  }) => {
    if (!conferenciaId) return;
    try {
      setSaving(true);
      await atualizarConferenciaVerificacao(conferenciaId, data);
      const detail = await getConferenciaVerificacao(conferenciaId);
      setConferencia(detail);
    } catch {
      // handled
    } finally {
      setSaving(false);
    }
  }, [conferenciaId]);

  // ── Register reading on backend ──

  const registrarNoBackend = useCallback(async (gs1: GS1Data) => {
    if (!conferenciaId) return;

    const gtin = gs1.gtin || '';
    const ref_code = gs1.refCode || undefined;

    console.log('[Scan] registrarNoBackend:', { gtin, ref_code, lote: gs1.lote, val: gs1.validade });

    try {
      const result = await registrarLeitura(conferenciaId, {
        gtin,
        ref_code,
        lote: gs1.lote || undefined,
        data_validade: gs1.validade ? gs1.validade.toISOString().split('T')[0] : undefined,
        serial: gs1.serie || undefined,
        // N92: manda o codigo EXATAMENTE como saiu do scanner. Sem isto, o
        // servidor so recebe o resultado do parse — e quando o parse erra, nao
        // sobra evidencia de qual codigo causou o erro.
        raw_barcode: gs1.rawData || undefined,
        scan_type: gs1.scanType || undefined,
        input_source: 'Web',
      });

      if (result.success) {
        playSuccess();
        setLastScannedItemId(result.item_id);

        const validadeStr = gs1.validade
          ? gs1.validade.toLocaleDateString('pt-BR')
          : null;

        setLastReadInfo({
          status: 'success',
          message: result.message,
          produtoNome: result.produto_nome,
          codigoProduto: result.codigo_produto || gs1.refCode || gs1.gtin,
          lote: result.lote || gs1.lote,
          validade: validadeStr,
          quantidade: result.quantidade_conferida,
        });
        updateLastLog('success', result.message);

        // Toast informativo por tipo de conferência
        if (conferencia?.tipo === 'LACRE') {
          if ((result.quantidade_esperada ?? 0) > 0) {
            toast.success(`Item contado: ${result.codigo_produto}`, { duration: 2000 });
          } else {
            toast(`Contado, mas não estava na coleta: ${result.codigo_produto}`, {
              icon: '\u26A0\uFE0F',
              duration: 3000,
              style: { background: '#FFF7ED', color: '#9A3412', border: '1px solid #FB923C' },
            });
          }
        } else {
          toast.success(`Item adicionado: ${result.codigo_produto}`, { duration: 2000 });
        }

        if (result.created || !result.item_id) {
          await reloadItens();
        } else if (result.item_id) {
          setItens(prev => prev.map(item =>
            item.id === result.item_id
              ? {
                  ...item,
                  quantidade_conferida: result.quantidade_conferida,
                  diferenca: result.quantidade_conferida - item.quantidade_esperada,
                  gtin_lido: gtin || item.gtin_lido,
                }
              : item
          ));
        }
      } else {
        playError();
        setLastReadInfo({
          status: 'error',
          message: result.message,
          codigoProduto: gs1.refCode || gs1.gtin,
        });
        updateLastLog('error', result.message);

        // Toast erro por tipo de conferência
        if (conferencia?.tipo === 'LACRE') {
          toast.error('Produto não encontrado no cadastro', { duration: 4000 });
        } else {
          toast.error('Item não está no cadastro do cliente', { duration: 4000 });
        }
      }
    } catch {
      playError();
      updateLastLog('error', 'Erro de rede');
    }
  }, [conferenciaId, conferencia, reloadItens, updateLastLog]);

  // ── Handle barcode scan (two-read state machine) ──

  const handleScan = useCallback(async (rawBarcode: string) => {
    if (!conferenciaId) return;

    // Init sound on first scan
    if (!soundReady) {
      await initSoundFeedback();
      setSoundReady(true);
    }

    // 1. Parse GS1
    const gs1 = parseGS1(rawBarcode);
    console.log('[Scan] parseGS1:', {
      raw: rawBarcode,
      gtin: gs1.gtin,
      refCode: gs1.refCode,
      lote: gs1.lote,
      val: gs1.validade,
      scanType: gs1.scanType,
    });

    // 2. Add initial log
    addScanLog(rawBarcode, gs1, 'processing', 'Processando...');

    // 3. Check if this is a second read that can merge with pending first read
    if (pendingFirstRead && isSecondRead(gs1) && canMerge(pendingFirstRead, gs1)) {
      console.log('[Scan] Merging with pending first read');
      const merged = mergeGS1Data(pendingFirstRead, gs1);
      clearPending();
      updateLastLog('merged', 'Merge com 1a leitura');
      await registrarNoBackend(merged);
      setTimeout(() => scanInputRef.current?.focus(), 100);
      return;
    }

    // 4. Check if this needs a second read (has GTIN/refCode but no lot)
    if (needsSecondRead(gs1)) {
      console.log('[Scan] Needs second read — entering pending state');
      clearPending(); // Clear any existing pending
      setPendingFirstRead(gs1);
      playAttention();

      const identifier = gs1.refCode || gs1.gtin || '?';
      toast(`Aguardando 2a leitura (lote/validade)... [${identifier}]`, { icon: '⏳', duration: 5000 });

      setLastReadInfo({
        status: 'waiting',
        message: `Aguardando 2a leitura... (${identifier})`,
        codigoProduto: identifier,
        countdownSeconds: Math.round(TWO_READ_TIMEOUT_MS / 1000),
      });
      updateLastLog('waiting', `Aguardando 2a leitura [${identifier}]`);

      // Countdown
      let remaining = Math.round(TWO_READ_TIMEOUT_MS / 1000);
      pendingCountdownRef.current = setInterval(() => {
        remaining -= 1;
        if (remaining <= 0) {
          if (pendingCountdownRef.current) clearInterval(pendingCountdownRef.current);
          return;
        }
        setLastReadInfo(prev =>
          prev && prev.status === 'waiting'
            ? { ...prev, countdownSeconds: remaining }
            : prev
        );
      }, 1000);

      // Timeout
      pendingTimeoutRef.current = setTimeout(() => {
        console.log('[Scan] Two-read timeout — clearing pending');
        playError();
        setPendingFirstRead(null);
        if (pendingCountdownRef.current) {
          clearInterval(pendingCountdownRef.current);
          pendingCountdownRef.current = null;
        }
        setLastReadInfo({
          status: 'error',
          message: `Timeout: 2a leitura não recebida para ${identifier}`,
          codigoProduto: identifier,
        });
        toast.error(`Timeout: 2a leitura não recebida`);
      }, TWO_READ_TIMEOUT_MS);

      setTimeout(() => scanInputRef.current?.focus(), 100);
      return;
    }

    // 5. Determine identifiers for single-read
    const gtin = gs1.gtin || '';
    const ref_code = gs1.refCode || undefined;

    if (!gtin && !ref_code) {
      playError();
      toast.error('Código sem GTIN ou referência');
      setLastReadInfo({ status: 'error', message: 'Código sem GTIN ou referência' });
      updateLastLog('error', 'Sem GTIN/refCode');
      setTimeout(() => scanInputRef.current?.focus(), 100);
      return;
    }

    // 6. Clear any pending (new complete scan overrides)
    if (pendingFirstRead) {
      clearPending();
    }

    // 7. Register on backend
    await registrarNoBackend(gs1);

    // Refocus scanner input
    setTimeout(() => scanInputRef.current?.focus(), 100);
  }, [conferenciaId, soundReady, pendingFirstRead, addScanLog, updateLastLog, clearPending, registrarNoBackend]);

  // ── Finalize ──

  const handleFinalize = useCallback(async () => {
    if (!conferenciaId) return false;
    try {
      const result = await finalizarConferencia(conferenciaId);
      if (result.success) {
        toast.success(result.message);
        await loadDetail();
        return true;
      }
      toast.error(result.message);
    } catch {
      // handled
    }
    return false;
  }, [conferenciaId, loadDetail]);

  // ── Toggle (MASTER reopen) ──

  const handleToggleConferido = useCallback(async () => {
    if (!conferenciaId) return;
    try {
      const result = await toggleConferido(conferenciaId);
      if (result.success) {
        toast.success(result.message);
        await loadDetail();
      }
    } catch {
      // handled
    }
  }, [conferenciaId, loadDetail]);

  // ── Edit mode handlers ──

  const handleEnterEditMode = useCallback(() => {
    setOriginalItens([...itens]);
    setModifiedItems(new Map());
    setIsEditMode(true);
  }, [itens]);

  const handleUpdateItemField = useCallback((itemId: number, _field: string, value: number) => {
    setItens(prev => prev.map(item => {
      if (item.id === itemId) {
        return {
          ...item,
          quantidade_conferida: value,
          diferenca: value - item.quantidade_esperada,
        };
      }
      return item;
    }));
    setModifiedItems(prev => {
      const newMap = new Map(prev);
      newMap.set(itemId, value);
      return newMap;
    });
  }, []);

  const handleSaveAllItems = useCallback(async () => {
    if (!conferenciaId || modifiedItems.size === 0) {
      toast('Nenhuma alteração para salvar');
      setIsEditMode(false);
      return;
    }

    try {
      setSaving(true);
      const errors: string[] = [];
      const promises: Promise<any>[] = [];

      modifiedItems.forEach((newQty, itemId) => {
        const promise = atualizarItemConferencia(conferenciaId, itemId, newQty, true)
          .catch(err => {
            errors.push(`Item ${itemId}: ${err.message}`);
            return null;
          });
        promises.push(promise);
      });

      await Promise.all(promises);

      if (errors.length > 0) {
        toast.error(`Erros ao salvar: ${errors.join(', ')}`);
        return;
      }

      toast.success(`${modifiedItems.size} item(s) atualizado(s) com sucesso`);
      setModifiedItems(new Map());
      setOriginalItens([]);
      setIsEditMode(false);
      await reloadItens();
    } catch {
      toast.error('Erro ao salvar alterações');
    } finally {
      setSaving(false);
    }
  }, [conferenciaId, modifiedItems, reloadItens]);

  const handleDiscardChanges = useCallback(() => {
    if (modifiedItems.size === 0) {
      toast('Nenhuma alteração para descartar');
      setIsEditMode(false);
      return;
    }
    setItens([...originalItens]);
    setModifiedItems(new Map());
    setOriginalItens([]);
    setIsEditMode(false);
    toast.success('Alterações descartadas');
  }, [modifiedItems, originalItens]);

  const handleReiniciar = useCallback(async () => {
    if (!conferenciaId) return;
    try {
      setSaving(true);
      const result = await reiniciarConferencia(conferenciaId);
      if (result.success) {
        await reloadItens();
        setIsEditMode(false);
        setModifiedItems(new Map());
        setOriginalItens([]);
      }
    } catch {
      // error handled in service
    } finally {
      setSaving(false);
    }
  }, [conferenciaId, reloadItens]);

  return {
    conferencia,
    itens,
    lacresDisponiveis,
    loading,
    loadingItens,
    saving,
    lastScannedItemId,
    soundReady,
    scanInputRef,
    // Two-read
    pendingFirstRead,
    // Feedback
    lastReadInfo,
    setLastReadInfo,
    scanLogs,
    clearScanLogs,
    // Actions
    loadDetail,
    reloadItens,
    handleInitSound,
    handleSelectLacres,
    handleUpdateFields,
    handleScan,
    handleFinalize,
    handleToggleConferido,
    // Edit mode
    isEditMode,
    modifiedItems,
    handleEnterEditMode,
    handleUpdateItemField,
    handleSaveAllItems,
    handleDiscardChanges,
    handleReiniciar,
  };
}
